import { useMarketWalletInfo } from '@/domain/wallet/useMarketWalletInfo'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useMyPortfolio as useIsolatedPortfolio } from '@/features/isolated-portfolio/logic/useMyPortfolio'
import { useMyPortfolio as usePooledPortfolio } from '@/features/my-portfolio/logic/useMyPortfolio'
import { useL1Balances } from '@/domain/wallet/hypercore/useL1Balances'

import { useMarketInfo } from '@/domain/market-info/useMarketInfo'
import { getChainConfigEntry } from '@/config/chain'
import { assert } from '@/utils/assert'
import { useHyperCoreSpotMeta } from '@/domain/wallet/hypercore/useHyperCoreSpotMeta'
import { useTokensInfo } from '@/domain/wallet/useTokens/useTokensInfo'
import {
  formatExtraTokens,
  formatTokenInfoAssets,
  formatL1Tokens,
  formatTokenData,
  getGroupedByPairAddress,
  getLowestHealthFactor,
  getTotalIsolatedUSD,
  usdFormatter,
  usdFormatterCompact,
} from './utils'
import { useIsolatedPairs } from '@/features/isolated/logic/useIsolatedPairs'
import { useIsolatedHealthFactor } from '@/features/isolated-details/logic/useIsolatedHealthFactor'
import { UseAccountOverviewResult } from './types'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { useSideDrawer } from '@/domain/hooks/useSideDrawer'
import { healthFactorToRiskLevel } from '@/domain/common/risk'
import { useEffect } from 'react'

export function useAccountOverview(): UseAccountOverviewResult {
  const { chainId } = usePageChainId()
  const { positionsTab } = useSideDrawer()
  const { highestRiskLevel, setHighestRiskLevel, setShowPositionsBadge } = positionsTab
  const walletInfo = useMarketWalletInfo({ chainId })
  const { marketInfo } = useMarketInfo({ chainId })
  const { pairs, isLoading: isIsolatedPairsLoading } = useIsolatedPairs()
  const { deposits: isolatedDeposits, borrows: isolatedBorrows } = useIsolatedPortfolio()
  const { deposits: pooledDeposits, borrows: pooledBorrows } = usePooledPortfolio()
  const { balances: l1Balances } = useL1Balances({ refetchInterval: 30000 })
  const { markets } = getChainConfigEntry(chainId)
  const { assets: hyperCoreAssets } = useHyperCoreSpotMeta()

  const { nativeAssetInfo } = markets ?? {}
  assert(nativeAssetInfo, 'Native asset info is required for overview')

  const pooledHealthFactor = marketInfo.userPositionSummary.healthFactor

  const {
    pairs: pairsWithHealth,
    minHealthFactor: isolatedMinHealthFactor,
    hasUnsafePositions,
  } = useIsolatedHealthFactor({
    pairAddresses: pairs.map((pair) => pair.address),
  })

  const tokensFormatted = formatTokenInfoAssets({
    hyperCoreAssets,
    nativeAssetInfo,
    walletInfo,
    isolatedPairs: pairs,
    isLoading: isIsolatedPairsLoading,
  })

  const { tokensInfo } = useTokensInfo({
    tokens: tokensFormatted,
    chainId,
  })

  const extraTokens = formatExtraTokens({
    tokensInfo,
    pairs,
  })

  const formattedL1Balances = formatL1Tokens({
    l1Balances,
    hyperCoreAssets,
    marketInfo,
    nativeAssetInfo,
  })

  const totalEVMTokenBalanceUSD = walletInfo.getTotalUSD()
  const totalExtraTokensUSD = extraTokens.reduce(
    (acc, t) => NormalizedUnitNumber(acc.plus(t.token.unitPriceUsd.multipliedBy(t.balance))),
    NormalizedUnitNumber(0),
  )
  const totalL1TokenBalanceUSD = formattedL1Balances.reduce(
    (acc, t) => NormalizedUnitNumber(acc.plus(t.token.unitPriceUsd.multipliedBy(t.balance))),
    NormalizedUnitNumber(0),
  )

  const totalPooledUSD = marketInfo.userPositionSummary.totalCollateralUSD.minus(
    marketInfo.userPositionSummary.totalBorrowsUSD,
  )

  const totalIsolatedUSD = getTotalIsolatedUSD(isolatedBorrows, isolatedDeposits)
  const totalTokenBalanceUSD = totalEVMTokenBalanceUSD.plus(totalExtraTokensUSD).plus(totalL1TokenBalanceUSD)

  const lowestHealthFactor = getLowestHealthFactor(pooledHealthFactor, isolatedMinHealthFactor)

  const currentHighestRiskLevel = healthFactorToRiskLevel(lowestHealthFactor)

  useEffect(() => {
    if (currentHighestRiskLevel !== highestRiskLevel) {
      setHighestRiskLevel(currentHighestRiskLevel)
      setShowPositionsBadge(true)
    }
  }, [currentHighestRiskLevel, highestRiskLevel, setHighestRiskLevel, setShowPositionsBadge])

  return {
    // Currently just the net position balance
    totalUSD: usdFormatter.format(totalPooledUSD.plus(totalIsolatedUSD).toNumber()),
    tokenData: {
      ...formatTokenData({
        chainId,
        walletTokens: [...walletInfo.walletBalances, ...extraTokens],
        formattedL1Balances,
        nativeAssetInfo,
      }),
      totalUSD: usdFormatter.format(totalTokenBalanceUSD.toNumber()),
    },
    positionData: {
      highestRiskLevel,
      pooled: {
        deposits: pooledDeposits.filter((reserve) => reserve.deposit.gt(0)),
        borrows: pooledBorrows.filter((reserve) => reserve.debt.gt(0)),
        healthFactor: marketInfo.userPositionSummary.healthFactor,
        totalUSD: usdFormatterCompact.format(totalPooledUSD.toNumber()),
      },
      isolated: {
        totalUSD: usdFormatterCompact.format(totalIsolatedUSD.toNumber()),
        minHealthFactor: isolatedMinHealthFactor,
        hasUnsafePositions,
        groupedByPairAddress: getGroupedByPairAddress({ pairs, isolatedDeposits, isolatedBorrows, pairsWithHealth }),
      },
    },
  }
}
